import React from 'react';
import { useNavigate } from 'react-router-dom';
import './ProductMentionRenderer.css';

/**
 * ProductMentionRenderer
 * 
 * Rendert Forum-Text mit #Produkt Mentions als klickbare Links
 * Erkennt Pattern wie #ProductName und macht sie zu klickbaren Mentions
 * Klick führt zur Produktseite mit Preis-Ranking
 */
export function ProductMentionRenderer({ text }) {
  const navigate = useNavigate();
  
  if (!text) return null;

  // Regex um #Produktname zu finden
  // Matches: # gefolgt von alphanumerisch/Spaces/Sonderzeichen bis doppeltem Space oder Newline
  // Erweitert für: Umlaute, ·, Zahlen, Bindestriche, etc.
  const parts = [];
  const mentionRegex = /#([\w\sÄÖÜäöüß·\-\.0-9]+?)(?=\s{2}|$|\n|[,;!?](?:\s|$))/g;
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    // Text vor dem Mention
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index)
      });
    }

    // Das Mention selbst
    parts.push({
      type: 'mention',
      productName: match[1].trim(),
      fullText: match[0]
    });

    lastIndex = mentionRegex.lastIndex;
  }

  // Restlicher Text nach dem letzten Mention
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }

  // Klick-Handler: Suche Produkt per API und navigiere zur Seite
  const handleMentionClick = async (productName) => {
    try {
      const response = await fetch(`/api/forum/products/search?query=${encodeURIComponent(productName)}`);
      const products = await response.json();
      
      // Finde das exakte oder beste Produkt
      const exactMatch = products.find(p => p.name.toLowerCase() === productName.toLowerCase());
      const product = exactMatch || products[0];
      
      if (product && product.id) {
        navigate(`/product/${product.id}/suche`);
      } else {
        console.warn('Produkt nicht gefunden:', productName);
      }
    } catch (err) {
      console.error('Fehler beim Suchen des Produkts:', err);
    }
  };

  return (
    <div className="product-mention-renderer">
      {parts.length === 0 ? (
        text
      ) : (
        parts.map((part, idx) =>
          part.type === 'mention' ? (
            <span
              key={idx}
              className="product-mention-badge clickable"
              onClick={() => handleMentionClick(part.productName)}
              title={`Klicke um ${part.productName} anzusehen`}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleMentionClick(part.productName);
                }
              }}
            >
              <span className="mention-icon">🏷️</span>
              <span className="mention-text">{part.productName}</span>
            </span>
          ) : (
            <span key={idx}>{part.content}</span>
          )
        )
      )}
    </div>
  );
}

export default ProductMentionRenderer;
